// Shared between the admin portal (AdminPanel.tsx) and each business's own
// read-only Settings > Subscription tab (Settings.tsx) — previously copied
// byte-for-byte in both files.

// Theme colours, not fixed pastels: the status colour as text, a 12% tint
// as the fill and a 30% tint as the outline, so the badge reads the same
// in light and dark theme (fixed light fills showed as bright blocks).
const tone = (c: string) => ({
  color: c,
  bg: `color-mix(in srgb, ${c} 12%, transparent)`,
  border: `1px solid color-mix(in srgb, ${c} 30%, transparent)`,
});

export function renewalBadge(end?: string): { label: string; bg: string; color: string; border: string } | null {
  if (!end) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((new Date(end).getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0)   return { label: 'Overdue',  ...tone('var(--danger)') };
  if (diffDays <= 30) return { label: 'Due Soon', ...tone('var(--warning)') };
  return               { label: 'On Track', ...tone('var(--success)') };
}

// Read a Date's own local Y/M/D back out as "YYYY-MM-DD" — the counterpart
// to parsing a date-only string as local midnight below. Deliberately NOT
// toISOString() (which always renders in UTC): round-tripping a local
// midnight Date through toISOString() shifts the calendar date back a day
// in any timezone ahead of UTC (e.g. India, UTC+5:30) — a real bug caught
// by devtest verification, since local midnight Sep 1 IST is still Aug 31
// in UTC. Every date-string helper here stays in local calendar-date space
// end to end so there's no UTC round trip to drift across.
function toDateOnlyString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// "YYYY-MM-DD" + 1 year, done on the calendar date directly (not via ms math,
// which would drift across leap years) — e.g. "2026-02-28" -> "2027-02-28".
export function addOneYear(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setFullYear(d.getFullYear() + 1);
  return toDateOnlyString(d);
}

// The new subscriptionEnd for a renewal recorded today: extends from
// whichever is later, the current end date or today — a renewal on a
// still-active subscription simply adds a year on top (no time lost by
// renewing early); a renewal on an overdue one resets from today instead of
// stacking onto a stale past date.
export function computeRenewalEnd(currentEnd: string | undefined | null): string {
  const todayStr = toDateOnlyString(new Date());
  const base = currentEnd && currentEnd > todayStr ? currentEnd : todayStr;
  return addOneYear(base);
}
